import type {
  Matching,
  MatchingCauseOut,
  MatchingFiguresOut,
  MatchingInvoiceOut,
  MatchingOrderLineOut,
  MatchingOrderOut,
  MatchingPairOut,
} from "../data/api-types";
import type { RecordView } from "./object-ui/contract";

/**
 * A purchase invoice's match group as its Matching section draws it (review
 * `docs/features/purchase-invoice-matching/13_matching_in_one_review.md` §5): the group's invoices and orders, how
 * each invoice line pairs with an order line, the group's figures and why they differ. Every value is the ledger's
 * own, composed by the views' record (`MatchingOut`): the section adds nothing up and decides nothing.
 */
export type MatchingView = Matching;
export type MatchingInvoice = MatchingInvoiceOut;
export type MatchingOrder = MatchingOrderOut;
export type MatchingOrderLine = MatchingOrderLineOut;
export type MatchingPair = MatchingPairOut;
export type MatchingFigures = MatchingFiguresOut;
export type MatchingCause = MatchingCauseOut;

export function matchingOf(record: RecordView): MatchingView | null {
  const value = record["matching"];
  return value && typeof value === "object" ? (value as MatchingView) : null;
}

/** The group's invoices with the one whose record this is first; the others keep the ledger's order. */
export function invoicesThisFirst(matching: MatchingView): MatchingInvoice[] {
  return [
    ...matching.invoices.filter((invoice) => invoice.is_this),
    ...matching.invoices.filter((invoice) => !invoice.is_this),
  ];
}

/** Whole pence as pounds: "£1,180.00", "-£15.00". */
export function formatPence(pence: number): string {
  const pounds = new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(pence) / 100);
  return pence < 0 ? `-${pounds}` : pounds;
}
